import { HttpClient } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  signal,
  ViewEncapsulation,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { OiqFormListComponent } from '@oiq-ui-sdk/form';
import { CollectionTableFilterBaseComponent } from './filter.component';
import { CollectionFilterDefinition } from './filter.models';
import {
  CollectionFilterFormatPayloadList,
  CollectionFilterFromQueryList,
  CollectionFilterListTransformer,
} from './filter.utils';

const _optionsCache: { [id: string]: { params: any; collection: any[] } } = {};

@Component({
  selector: 'collection-filter-list',
  standalone: true,
  imports: [ReactiveFormsModule, OiqFormListComponent],
  template: `
    @let _definition = definition();
    @if (_definition) {
      <oiq-form-list
        class="collection-filter-list collection_filter_list_{{
          _definition.class || _definition.key
        }}"
        [placeholder]="_definition.label"
        [options]="options()"
        [searchable]="_definition.list.searchable"
        [multi]="_definition.list.multi"
        [canClear]="_definition.list.canClear"
        [excludeSelected]="_definition.list.excludeSelected"
        [prefixIcon]="_definition.list.prefixIcon"
        [suffixIcon]="_definition.list.suffixIcon"
        [virtual]="_definition.list.virtual || false"
        [itemsVisible]="_definition.list.itemsVisible"
        [itemHeight]="_definition.list.itemHeight"
        [itemsWidth]="_definition.list.itemsWidth"
        [configuration]="listConfiguration()"
        [formControl]="internalControl"
        (event)="onValueChange($event)">
        <ng-template #listItem let-item>
          <div class="__text">
            <span
              class="{{ _definition.list.itemClass }} {{
                item.id.toString().toLowerCase()
              }} __id__{{ item.id.toString().toLowerCase() }}"
              >{{ item.__text__ || item.text }}</span
            >
          </div>
        </ng-template>
      </oiq-form-list>
    }
  `,
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CollectionFilterListComponent extends CollectionTableFilterBaseComponent {
  private _http = inject(HttpClient);
  internalControl = new FormControl<any[]>([]);

  public options = signal<any[]>([]);
  public listConfiguration = computed(() =>
    this.definition()?.list?.transformers?.length
      ? { id: '__id__', text: '__text__' }
      : this.definition()?.list?.configuration
  );
  constructor() {
    super();
    effect(() => {
      const _value = this.value();
      const _definition = this.definition();
      if (!_definition) {
        return;
      }
      const _normalized = CollectionFilterFromQueryList(_value, _definition, false) as any;
      if (this.internalControl.value === _normalized) {
        return;
      }
      this.internalControl.setValue(_normalized || null, {
        emitEvent: false,
      });
    });
    effect(() => {
      const _definition = this.definition();

      switch (_definition.list.source) {
        case 'http':
          // cache first choice
          const _cached = _optionsCache[_definition.list.http.url];
          if (
            _cached &&
            JSON.stringify(_cached.params) === JSON.stringify(_definition.list.http.params)
          ) {
            this.options.set(_cached.collection);
          }
          // fetch from http to refresh the list
          const _params = _definition.list.http.params || {};
          const _url = _definition.list.http.url;
          this._http
            .get<any>(_url, {
              params: _params,
            })
            .subscribe((response: any) => {
              const _collection = response.content ? response.content : response;

              _optionsCache[_url] = {
                params: _params,
                collection: CollectionFilterListTransformer(
                  _collection,
                  _definition.list.transformers,
                  _definition.list.configuration
                ),
              };
              this.options.set(_optionsCache[_url].collection);
            });
          break;
        default:
          this.options.set(_definition.list.options);
      }
    });
  }

  getQuery() {
    return CollectionFilterFormatPayloadList(this.internalControl.value, this.definition());
  }

  onValueChange(value: { type: string; data?: any }): void {
    switch (value.type) {
      case 'change':
        this.handleValueChanged();
    }
  }

  static fromQuery(query: any, definition: CollectionFilterDefinition): { [key: string]: any } {
    return CollectionFilterFromQueryList(query, definition, true);
  }
}
